class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.infoDrawerOpen) {
        this.closeInfoDrawer();
        return;
      }
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupInfoDrawer();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setupInfoDrawer() {
    this.infoDrawer = this.querySelector('.cart-info-drawer');
    if (!this.infoDrawer) return;

    this.infoDrawerContent = this.infoDrawer.querySelector('.cart-info-drawer__content');
    this.infoDrawerTitle = this.infoDrawer.querySelector('.cart-info-drawer__title');
    this.infoDrawerBack = this.infoDrawer.querySelector('.cart-info-drawer__back');
    this.infoDrawerOverlay = this.infoDrawer.querySelector('.cart-info-drawer__overlay');
    this.infoDrawerPanel = this.infoDrawer.querySelector('.cart-info-drawer__panel');

    if (!this.infoDrawerTriggerAttached) {
      this.addEventListener('click', this.onInfoDrawerTrigger.bind(this));
      this.infoDrawerTriggerAttached = true;
    }

    if (this.infoDrawerBack) this.infoDrawerBack.addEventListener('click', this.closeInfoDrawer.bind(this));
    if (this.infoDrawerOverlay) this.infoDrawerOverlay.addEventListener('click', this.closeInfoDrawer.bind(this));
    this.infoDrawerOpen = false;
  }

  onInfoDrawerTrigger(event) {
    const opener = event.target.closest('[data-cart-info-target]');
    if (!opener) return;

    event.preventDefault();
    const targetSelector = opener.getAttribute('data-cart-info-target');
    const target = this.querySelector(targetSelector);
    if (!target) return;

    const title = opener.getAttribute('data-cart-info-title') || opener.textContent.trim();
    if (this.infoDrawerTitle) this.infoDrawerTitle.textContent = title;
    if (this.infoDrawerContent) this.infoDrawerContent.innerHTML = target.innerHTML;
    this.openInfoDrawer(opener);
  }

  openInfoDrawer(opener) {
    this.infoDrawerOpener = opener;
    this.infoDrawer.classList.remove('opacity-0', 'invisible', 'pointer-events-none');
    this.infoDrawer.classList.add('opacity-100', 'visible', 'pointer-events-auto');
    if (this.infoDrawerPanel) {
      this.infoDrawerPanel.classList.remove('translate-x-full');
      this.infoDrawerPanel.classList.add('translate-x-0');
    }
    this.infoDrawer.setAttribute('aria-hidden', 'false');
    this.infoDrawerOpen = true;
    trapFocus(this.infoDrawer, this.infoDrawerBack);
  }

  closeInfoDrawer() {
    this.infoDrawer.classList.remove('opacity-100', 'visible', 'pointer-events-auto');
    this.infoDrawer.classList.add('opacity-0', 'invisible', 'pointer-events-none');
    if (this.infoDrawerPanel) {
      this.infoDrawerPanel.classList.remove('translate-x-0');
      this.infoDrawerPanel.classList.add('translate-x-full');
    }
    this.infoDrawer.setAttribute('aria-hidden', 'true');
    if (this.infoDrawerContent) this.infoDrawerContent.innerHTML = '';
    this.infoDrawerOpen = false;
    removeTrapFocus(this.infoDrawerOpener);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.setupInfoDrawer();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
